import { useEffect, useState } from 'react';
import { UniversalPage, Card, NotificationCard, StatusBadge, type StatusTone } from '@afrilink/ui';
import {
  adminReportsService,
  type LifecycleFunnelRow,
} from '@/domains/reports/services/adminReportsService';
import { formatZARWhole } from '@/shared/utils/format';

const STAGE_TONE: Record<string, StatusTone> = {
  lead: 'info',
  active: 'active',
  suspended: 'overdue',
  cancellation_pending: 'pending',
  churned: 'draft',
};

const STAGE_LABEL: Record<string, string> = {
  lead: 'Lead',
  active: 'Active',
  suspended: 'Suspended',
  cancellation_pending: 'Cancelling',
  churned: 'Churned',
};

export default function LifecycleFunnelPage() {
  const [data, setData] = useState<LifecycleFunnelRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminReportsService
      .getLifecycleFunnel()
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  const total = (data ?? []).reduce((s, r) => s + r.customer_count, 0);
  const totalMrr = (data ?? []).reduce((s, r) => s + Number(r.mrr_in_stage ?? 0), 0);

  return (
    <UniversalPage
      title="Lifecycle funnel"
      breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: 'Reports', href: '/admin/reports' },
        { label: 'Lifecycle' },
      ]}
    >
      {error && <NotificationCard tone="danger" title="Could not load funnel" body={error} />}

      <Card interactive={false}>
        <div className="border-b border-border p-4">
          <p className="text-sm text-muted-foreground">
            Customers grouped by <code>customer_profile.lifecycle_stage</code>. MRR is summed from
            each customer's active <code>customer_link.service_plan</code>, normalised to monthly.
          </p>
        </div>
        <div className="divide-y divide-border">
          {(data ?? []).map((row) => {
            const pct = total > 0 ? (row.customer_count / total) * 100 : 0;
            return (
              <div key={row.lifecycle_stage} className="space-y-2 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <StatusBadge tone={STAGE_TONE[row.lifecycle_stage] ?? 'draft'}>
                      {STAGE_LABEL[row.lifecycle_stage] ?? row.lifecycle_stage}
                    </StatusBadge>
                    <span className="text-lg font-semibold text-foreground">{row.customer_count}</span>
                    <span className="text-xs text-muted-foreground">
                      ({pct.toFixed(1)}% of {total})
                    </span>
                  </div>
                  <span className="text-sm font-medium text-foreground">
                    {formatZARWhole(Number(row.mrr_in_stage ?? 0))} MRR
                  </span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full bg-primary transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
          {data?.length === 0 && (
            <p className="p-8 text-center text-sm text-muted-foreground">No customers yet.</p>
          )}
        </div>
        <div className="border-t border-border bg-card-subtle px-4 py-3 text-sm">
          <span className="text-muted-foreground">Total: </span>
          <strong className="text-foreground">{total} customers</strong>
          <span className="text-muted-foreground"> · </span>
          <strong className="text-foreground">{formatZARWhole(totalMrr)} MRR</strong>
        </div>
      </Card>
    </UniversalPage>
  );
}
